/**
 * Client-side port of "find the next open appointment slot," used only
 * offline. Deliberately uses TRUE interval overlap for the blocked/booked
 * checks (`start &lt; otherEnd && end &gt; otherStart`), matching what the
 * server's create-time validation enforces (scheduling.Appointment.clean /
 * AppointmentCreateSerializer.validate, ClinicAppointmentCreateSerializer.
 * validate) -- NOT the clinic availability endpoint's read algorithm
 * (ClinicViewSet.availability), which uses a looser "does the slot's start
 * time fall inside the blocked/booked range" test. A slot that looks free
 * under that looser test can still get rejected on sync; matching the
 * stricter create-time semantics here means what we pick is what the
 * server will actually accept.
 *
 * Callers pre-filter `existingAppointments` to only status values that
 * actually block a slot on that backend (solo: exclude 'cancelled';
 * clinic: keep only 'pending'/'confirmed') -- this module doesn't know
 * about status semantics, just date/time ranges.
 */

import { dateToLocalYMD, toBackendDayOfWeek } from '../utils/date';

export interface SlotTimeBlock {
  start_time: string; // "HH:MM" or "HH:MM:SS"
  end_time: string;
  appointment_duration: number; // minutes
}

export interface SlotWorkingDay {
  day_of_week: number; // 0=Monday..6=Sunday, matching Python's date.weekday()
  time_blocks: SlotTimeBlock[];
}

/** A blocked range. start_time/end_time both null means the whole day is blocked. */
export interface SlotBlockedRange {
  date: string; // "YYYY-MM-DD"
  start_time: string | null;
  end_time: string | null;
}

export interface SlotExistingAppointment {
  date: string;
  start_time: string;
  end_time: string;
}

export interface FoundSlot {
  date: string;
  start_time: string; // "HH:MM:SS"
  end_time: string; // "HH:MM:SS"
  duration: number;
}

const SEARCH_DAYS = 42; // matches Calendar.tsx's existing 6-week booking window
const MIN_LEAD_MINUTES = 120; // matches Calendar.tsx's existing 2-hour buffer

function toMinutes(hhmm: string): number {
  const [h, m] = hhmm.split(':').map(Number);
  return h * 60 + m;
}

function fromMinutes(totalMinutes: number): string {
  const h = Math.floor(totalMinutes / 60)
    .toString()
    .padStart(2, '0');
  const m = (totalMinutes % 60).toString().padStart(2, '0');
  return `${h}:${m}:00`;
}

function rangesOverlap(aStart: number, aEnd: number, bStart: number, bEnd: number): boolean {
  return aStart < bEnd && aEnd > bStart;
}

export function findNextAvailableSlot(params: {
  workingDays: SlotWorkingDay[];
  blockedDates: SlotBlockedRange[];
  existingAppointments: SlotExistingAppointment[]; // already filtered to blocking statuses
  now?: Date;
}): FoundSlot | null {
  const now = params.now ?? new Date();
  const earliestStart = new Date(now.getTime() + MIN_LEAD_MINUTES * 60 * 1000);

  const workingDaysByDow = new Map<number, SlotWorkingDay>();
  for (const wd of params.workingDays) {
    workingDaysByDow.set(wd.day_of_week, wd);
  }

  for (let dayOffset = 0; dayOffset < SEARCH_DAYS; dayOffset++) {
    const candidateDate = new Date(now);
    candidateDate.setDate(candidateDate.getDate() + dayOffset);
    const dateStr = dateToLocalYMD(candidateDate);

    const blockedForDay = params.blockedDates.filter((b) => b.date === dateStr);
    if (blockedForDay.some((b) => b.start_time === null && b.end_time === null)) {
      continue; // full day blocked
    }

    const workingDay = workingDaysByDow.get(toBackendDayOfWeek(candidateDate));
    if (!workingDay) continue;

    const appointmentsForDay = params.existingAppointments.filter((a) => a.date === dateStr);

    for (const block of workingDay.time_blocks) {
      const blockStart = toMinutes(block.start_time);
      const blockEnd = toMinutes(block.end_time);
      const duration = block.appointment_duration;

      for (let slotStart = blockStart; slotStart + duration <= blockEnd; slotStart += duration) {
        const slotEnd = slotStart + duration;

        const isBlocked = blockedForDay.some((b) => {
          if (b.start_time === null || b.end_time === null) return false; // full-day already handled above
          return rangesOverlap(slotStart, slotEnd, toMinutes(b.start_time), toMinutes(b.end_time));
        });
        if (isBlocked) continue;

        const isBooked = appointmentsForDay.some((a) =>
          rangesOverlap(slotStart, slotEnd, toMinutes(a.start_time), toMinutes(a.end_time))
        );
        if (isBooked) continue;

        const slotStartDate = new Date(candidateDate);
        slotStartDate.setHours(0, slotStart, 0, 0);
        if (slotStartDate < earliestStart) continue;

        return {
          date: dateStr,
          start_time: fromMinutes(slotStart),
          end_time: fromMinutes(slotEnd),
          duration,
        };
      }
    }
  }

  return null;
}
